import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProgressTracker from '../../../components/ui/ProgressTracker';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const UploadQueue = ({ 
  files = [], 
  onRemoveFile, 
  onRetryFile, 
  onClearQueue,
  isProcessing = false,
  className = '' 
}) => {
  const modelLabelMap = {
    'gemini-2.0-flash-exp': 'Gemini 2.0 Flash (Experimental)',
    'gemini-2.0-flash': 'Gemini 2.0 Flash (Stable)'
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'FileText';
      case 'jpg': case 'jpeg': case 'png':
        return 'Image';
      default:
        return 'File';
    }
  };

  const getStatusConfig = (file) => {
    switch (file?.status) {
      case 'completed':
        return { status: 'reviewed', confidence: file?.confidence };
      case 'processing':
        return { status: 'processing' };
      case 'error':
        return { status: 'error' };
      case 'queued':
        return { status: 'pending' };
      default:
        return { status: 'pending' };
    }
  };

  const completedFiles = files?.filter(f => f?.status === 'completed')?.length;
  const errorFiles = files?.filter(f => f?.status === 'error')?.length;
  const processingFiles = files?.filter(f => f?.status === 'processing')?.length;

  if (files?.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg p-8 text-center ${className}`}>
        <div className="flex flex-col items-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground mb-1">No files in queue</h3>
            <p className="text-xs text-muted-foreground">
              Upload files to see them appear here for processing
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Queue" size={18} className="text-muted-foreground" />
          <div>
            <h3 className="text-sm font-medium text-foreground">Upload Queue</h3>
            <p className="text-xs text-muted-foreground">
              {files?.length} file{files?.length !== 1 ? 's' : ''} • 
              {completedFiles} completed • 
              {errorFiles} failed • 
              {processingFiles} processing
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {files?.length > 0 && !isProcessing && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearQueue}
              iconName="Trash2"
              iconPosition="left"
            >
              Clear Queue
            </Button>
          )}
        </div>
      </div>
      {/* File List */}
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {files?.map((file, index) => (
          <div key={file?.id || index} className="p-4">
            <div className="flex items-center space-x-3">
              {/* File Icon */}
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Icon 
                  name={getFileIcon(file?.name)} 
                  size={18} 
                  className="text-muted-foreground" 
                />
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {file?.name}
                  </h4>
                  <StatusIndicator 
                    {...getStatusConfig(file)}
                    size="sm"
                    variant={file?.confidence ? 'confidence' : 'default'}
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>{formatFileSize(file?.size)}</span>
                  {file?.processingTime && (
                    <span>Processed in {formatDuration(file?.processingTime)}</span>
                  )}
                  {file?.estimatedTime && file?.status === 'processing' && (
                    <span>{formatDuration(file?.estimatedTime)} remaining</span>
                  )}
                </div>

                {/* Progress Bar */}
                {file?.status === 'processing' && (
                  <ProgressTracker
                    progress={file?.progress || 0}
                    status="processing"
                    variant="compact"
                    size="sm"
                    label={`Processing ${file?.name}`}
                    estimatedTime={file?.estimatedTime || 0}
                    onCancel={() => onRemoveFile(file?.id)}
                  />
                )}

                {file?.modelUsed && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    OCR Model: {modelLabelMap[file?.modelUsed] || file?.modelUsed}
                  </div>
                )}

                {/* Error Message */}
                {file?.status === 'error' && file?.error && (
                  <div className="mt-2 p-2 bg-error/5 border border-error/20 rounded text-xs text-error">
                    <Icon name="AlertCircle" size={12} className="inline mr-1" />
                    {file?.error}
                  </div>
                )}

                {/* Success Info */}
                {file?.status === 'completed' && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-success">
                      <Icon name="CheckCircle" size={12} className="inline mr-1" />
                      {file?.invoiceCount || 1} invoice{(file?.invoiceCount || 1) !== 1 ? 's' : ''} extracted
                    </span>
                    {file?.confidence && (
                      <span className="text-muted-foreground">
                        Avg confidence: {Math.round(file?.confidence * 100)}%
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-1 flex-shrink-0">
                {file?.status === 'error' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRetryFile(file?.id)}
                    iconName="RotateCcw"
                    title="Retry processing"
                  />
                )}
                
                {file?.status !== 'processing' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveFile(file?.id)}
                    iconName="X"
                    title="Remove from queue"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Queue Summary */}
      {files?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-foreground">{files?.length}</div>
              <div className="text-xs text-muted-foreground">Total Files</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-success">{completedFiles}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-error">{errorFiles}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-primary">{processingFiles}</div>
              <div className="text-xs text-muted-foreground">Processing</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadQueue;
